/* eslint-disable no-param-reassign */
import { Icon } from '@zoom/components';
import classNames from 'classnames';
import React, { memo } from 'react';

import { WARNING_BACKGROUND_COLOR } from './config';
import { useAuth } from '../../../../app/AuthorizationProvider';
import { RecordBooleanType } from '../../../../types';
import { CHART_15_MIN_INTERVAL_WIDTH, MIN_15 } from '../../config';
import { CalculatedScheduleDataPerQueue } from '../../utils/mapChartData';

interface XAxisLabelsProps {
  dataPerQueue?: CalculatedScheduleDataPerQueue;
  dataPerQueueVisibility: RecordBooleanType;
  end: number;
  interval?: number;
  chartIntervalWidth?: number;
  start: number;
}

const XAxisLabelsComponent: React.FC<XAxisLabelsProps> = ({
  dataPerQueue,
  dataPerQueueVisibility,
  end,
  chartIntervalWidth = CHART_15_MIN_INTERVAL_WIDTH,
  interval = MIN_15,
  start,
}) => {
  const { locale } = useAuth();

  let currentInterval = start;
  const ticks = [];

  if (!dataPerQueue) {
    return null;
  }

  while (currentInterval <= end) {
    ticks.push(currentInterval);
    currentInterval += interval;
  }

  let marginAccumulator = 0;

  return (
    <div className="scheduling-chart__x-axis-container">
      {ticks.map((tick) => {
        // underStaffed and overStaffed values should contain sum of appropriate netStaff values of visible queues only

        /**
         * algorithm prepares both total and partial values (so we could show explicitly
         * over staffed / under staffed values of individual queues within tooltip)
         */
        const { underStaffed, overStaffed } = Object.keys(dataPerQueue).reduce(
          (totalValue, queueId) => {
            if (dataPerQueue[queueId].netStaff[tick] !== undefined && dataPerQueueVisibility[queueId]) {
              const value = dataPerQueue[queueId].netStaff[tick];

              if (value > 0) {
                totalValue.overStaffed.partial[queueId] = Math.floor(value * 100) / 100;
                totalValue.overStaffed.total += value;
              } else {
                const absValue = Math.abs(value);
                totalValue.underStaffed.partial[queueId] = Math.floor(absValue * 100) / 100;
                totalValue.underStaffed.total += absValue;
              }
            }

            return totalValue;
          },
          {
            underStaffed: { total: 0, partial: {} as Record<string, number> },
            overStaffed: { total: 0, partial: {} as Record<string, number> },
          }
        );

        if (underStaffed.total === 0 && overStaffed.total === 0) {
          marginAccumulator += chartIntervalWidth;
          return null;
        }

        const underStaffedTotalTruncated = Math.floor(underStaffed.total * 100) / 100;
        const overStaffedTotalTruncated = Math.floor(overStaffed.total * 100) / 100;

        const marginLeft = marginAccumulator || undefined;
        marginAccumulator = 0;

        const underStaffedValueDigitCount = underStaffedTotalTruncated.toString().length;
        const overStaffedValueDigitCount = overStaffedTotalTruncated.toString().length;

        return (
          <div className="scheduling-chart__x-axis-item-wrapper" key={tick} style={{ marginLeft }}>
            {overStaffedTotalTruncated > 0 ? (
              <div
                className={classNames('scheduling-chart__x-axis-item', {
                  'scheduling-chart__x-axis-item--s': overStaffedValueDigitCount > 2,
                })}
              >
                <Icon kind="root-arrow-up" size={8} color={WARNING_BACKGROUND_COLOR} />
                <span>{overStaffedTotalTruncated.toLocaleString(locale)}</span>
              </div>
            ) : null}
            {underStaffedTotalTruncated > 0 ? (
              <div
                className={classNames('scheduling-chart__x-axis-item', {
                  'scheduling-chart__x-axis-item--reversed': overStaffedTotalTruncated === 0,
                  'scheduling-chart__x-axis-item--s': underStaffedValueDigitCount > 2,
                })}
              >
                <Icon kind="root-arrow-down" size={8} color={WARNING_BACKGROUND_COLOR} />
                <span>{underStaffedTotalTruncated.toLocaleString(locale)}</span>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export const XAxisLabels = memo(XAxisLabelsComponent);
